import { getAxiosClient } from '~src/api';
import { getUser } from '~src/user';

export const GIOTTO_PERSONALIZATION_TITLE_MAX_LENGTH = 200;
export const GIOTTO_PERSONALIZATION_DESCRIPTION_MAX_LENGTH = 5000;
export const GIOTTO_PERSONALIZATION_NAME_MAX_LENGTH = 120;
export const GIOTTO_PERSONALIZATION_MAX_FILES = 5;
export const GIOTTO_PERSONALIZATION_MAX_FILE_SIZE = 15 * 1024 * 1024;
export const GIOTTO_PERSONALIZATION_ALLOWED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
  '.json',
  '.xml',
  '.zip',
];

export class GiottoPersonalizationClientError extends Error {
  constructor(status, code) {
    super(`Giotto personalization request failed with status ${status}`);
    this.name = 'GiottoPersonalizationClientError';
    this.status = status;
    this.code = code;
  }
}

export const hasAllowedGiottoPersonalizationExtension = (fileName) => {
  const normalizedName = fileName?.trim().toLowerCase();
  return Boolean(
    normalizedName &&
      GIOTTO_PERSONALIZATION_ALLOWED_EXTENSIONS.some((extension) =>
        normalizedName.endsWith(extension)
      )
  );
};

const parseResponseBody = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

const uploadPersonalizationAttachment = async (file, userToken) => {
  const edgeResizer = process.env.EDGE_RESIZER?.replace(/\/$/, '');
  if (!edgeResizer) {
    throw new Error('Il CDN degli allegati non è configurato');
  }

  const presignResponse = await fetch(
    `${process.env.PARSE_SERVER_ENDPOINT}/functions/upload`,
    {
      method: 'post',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-parse-application-id': process.env.PARSE_APPLICATION_ID,
        'x-parse-session-token': userToken,
      },
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        collection: 'personalization-requests',
        bucket: 'default',
        dir: '',
      }),
    }
  );
  const presignData = await parseResponseBody(presignResponse);
  if (!presignResponse.ok || !presignData.result?.url) {
    throw new Error('Impossibile preparare il caricamento degli allegati');
  }

  const uploadResponse = await fetch(presignData.result.url, {
    method: presignData.result.method || 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!uploadResponse.ok) {
    throw new Error('Impossibile caricare gli allegati');
  }

  const s3Key =
    presignData.result.s3key ||
    new URL(presignData.result.url).pathname.replace(/^\//, '');
  return {
    name: file.name,
    type: file.type || 'application/octet-stream',
    url: `${edgeResizer}/${s3Key}`,
  };
};

export const createGiottoPersonalization = async ({
  action,
  description,
  files = [],
  object,
  orientation,
  parentReference,
  reference,
  requestedName,
  title,
}) => {
  const { userToken } = await getUser();
  const attachments = await Promise.all(
    files.map((file) => uploadPersonalizationAttachment(file, userToken))
  );
  const httpClient = await getAxiosClient();

  try {
    await httpClient.post(`${process.env.GIOTTO_API_URL}/personalizations`, {
      action,
      attachments,
      description,
      object,
      ...(orientation ? { orientation } : {}),
      ...(parentReference ? { parentReference } : {}),
      ...(reference ? { reference } : {}),
      ...(requestedName ? { requestedName } : {}),
      title,
    });
  } catch (error) {
    const status = error.response?.status || 0;
    const code = error.response?.data?.error;
    throw new GiottoPersonalizationClientError(status, code);
  }
};
